import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';

/* ---------------------------------------------------------------------------
   Generated text, rendered.

   The assistant's replies are markdown, so a bubble has to turn them into
   markup. This is the one place that happens: both chat surfaces render through
   here, and any later surface that shows generated text joins them rather than
   importing a parser of its own. A second `MarkdownIt` instance somewhere else
   is how two bubbles end up disagreeing about what a bullet list looks like.

   Three decisions, each with a reason that is not "it looked fine":

   1. The parser lives in the page bundle. It is about 40 KB on the wire, which
      is less than a second request and the flash of escaped text that request
      used to buy. A reply is markdown on the first frame, including for a
      visitor who never opens the chat.

   2. `html: false` and the `heading` / `lheading` / `image` rules disabled. The
      reply is untrusted: it is model output. So raw HTML is escaped instead of
      interpreted, `#` never becomes a heading because a bubble carries one type
      scale, and `![](...)` never becomes an image because an external image is
      an outbound request the visitor did not ask for and a hallucinated URL
      would fetch silently in their browser.

   3. `DOMPurify` runs over the result, the same sanitiser the blog post and the
      automation preview already use. The parser is not a sanitiser, and
      `html: false` is not a sanitiser either: it stops the model writing tags,
      but it says nothing about what a link is allowed to point at.

   The link policy, and the trap it exists to avoid. `smoothScroll.js` installs
   a capture-phase click listener on the document that calls `preventDefault()`
   on any same-origin anchor carrying a hash and re-routes it through Inertia.
   So a blanket `target="_blank"` on our own links is either defeated or turned
   into a same-tab navigation the visitor did not ask for. The rule therefore
   splits by origin:

     * A same-origin link (including a relative path such as /web-design, which
       is what the prompt asks the model to write) carries no `target`. The
       site's own router handles the click, the navigation is an Inertia visit
       with no full reload, and the panel and the thread survive it. That is
       also what a visitor expects from a link to another page of this site.
     * Anything else gets `target="_blank"` with a full `rel`, because it is
       leaving the site and a new tab is the polite way to leave.
     * Only `http`, `https`, `mailto` and `tel` survive at all. Anything else is
       rendered as the words with no anchor, so a prompt-injected `javascript:`
       link is inert and an empty href cannot reload the page. A protocol-relative
       `//host` is an absolute URL, not a path on this site.

   `target` is added with `ADD_ATTR` because it is not in DOMPurify's default
   attribute list: without it the sanitiser would quietly strip the target off
   every external link, which is exactly the kind of half-applied change that
   looks like it works.
--------------------------------------------------------------------------- */

const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:', 'tel:'];

const SANITISE = {
  ADD_ATTR: ['target'],
};

const parser = new MarkdownIt({
  html: false,
  linkify: true,
  breaks: true,
});

parser.disable(['image', 'lheading', 'heading']);

const renderLink = parser.renderer.rules.link_open
  || ((tokens, index, options, env, self) => self.renderToken(tokens, index, options));

const renderClose = parser.renderer.rules.link_close
  || ((tokens, index, options, env, self) => self.renderToken(tokens, index, options));

parser.renderer.rules.link_open = (tokens, index, options, env, self) => {
  const href = tokens[index].attrGet('href') || '';

  if (!isAllowedHref(href)) {
    /* A span, not an anchor with an empty href. An empty href is still a link,
       and it reloads the page the visitor is on. The words stay, which is
       honest about a target that was not allowed to be one. */
    env.blockedLinks = (env.blockedLinks || 0) + 1;

    return '<span>';
  }

  if (!isSameOrigin(href)) {
    /* Leaving the site, so it opens on its own and does not take the
       thread with it. `nofollow` because nobody vouched for the target. */
    tokens[index].attrSet('target', '_blank');
    tokens[index].attrSet('rel', 'noopener noreferrer nofollow');
  }

  return renderLink(tokens, index, options, env, self);
};

parser.renderer.rules.link_close = (tokens, index, options, env, self) => {
  if (env.blockedLinks) {
    env.blockedLinks -= 1;

    return '</span>';
  }

  return renderClose(tokens, index, options, env, self);
};

/* Renders one reply. The result is safe HTML: the parser escapes raw tags,
   and DOMPurify is what decides which links may stay. */
export function renderMarkdown(text) {
  const source = String(text == null ? '' : text);

  if (source === '') {
    return '';
  }

  return DOMPurify.sanitize(parser.render(source), SANITISE);
}

function isAllowedHref(href) {
  const value = String(href || '').trim();

  if (value === '') {
    return false;
  }

  /* A relative path or a fragment has no scheme at all, so it is ours by
     definition and there is nothing to check it against. A protocol-relative
     URL (`//host`) is not one of those: it takes the page's scheme and leaves
     the site, so it is checked as an absolute URL. */
  if (isProtocolRelative(value)) {
    try {
      return ALLOWED_PROTOCOLS.indexOf(new URL(window.location.protocol + value).protocol.toLowerCase()) !== -1;
    } catch (error) {
      return false;
    }
  }

  if (!/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    return true;
  }

  try {
    return ALLOWED_PROTOCOLS.indexOf(new URL(value).protocol.toLowerCase()) !== -1;
  } catch (error) {
    return false;
  }
}

function isProtocolRelative(value) {
  return value.startsWith('//');
}

function isSameOrigin(href) {
  const value = String(href || '').trim();

  if (value === '') {
    return true;
  }

  /* Same test as isAllowedHref: `//host` is absolute, not a path on this site. */
  if (!isProtocolRelative(value) && !/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    return true;
  }

  try {
    const url = new URL(value, window.location.href);

    return url.origin === window.location.origin;
  } catch (error) {
    return false;
  }
}
